/**
 * Comparing two copies of one workspace, table by table and row by row.
 *
 * The `.edb` layer could only ever answer "which whole FILE is newer", so the
 * only two answers it could offer were "take mine" and "take theirs" — and one
 * of those always threw away work somebody did. This module is the finer
 * question: WHICH tables differ, and inside one of those, WHICH rows.
 *
 * ## Timestamps are all there is
 *
 * There is no change log and there are no tombstones. A copy of a workspace
 * knows what it holds and when each part was last written, and nothing about
 * what it used to hold. Two consequences run through everything below:
 *
 * - **A missing row is not a deleted row.** A row here and not there was either
 *   added here or deleted there, and the two are indistinguishable. So `newest`
 *   never deletes: it settles the rows BOTH sides have, and keeps the rest. The
 *   only thing that deletes is the user explicitly saying "make this side match
 *   the other one".
 * - **The comparison is by timestamp, not by value.** Two rows with the same
 *   stamp are treated as the same row, and rows are not read to check. A file
 *   edited by a machine with a wrong clock compares wrong, which is a real
 *   limitation and not one worth a content hash of every row to fix.
 *
 * Pure: no I/O, no store, no DOM. Both callers — the browser's live database
 * and a `.edb`'s bytes in a scratch worker — hand it the same two shapes.
 *
 * `diffRows` and `planRows` are reused as-is for view templates and view
 * instances too: a view document is `{id, updatedAt}`, precisely a `RowStamp`,
 * so it needs no diff logic of its own. See `replicate-run.ts`.
 */
/**
 * When the table was last touched AT ALL — the doc or any row in it.
 *
 * Editing a cell does not write the `tables` doc, so the doc's own stamp alone
 * would call a table with an hour of edits in it unchanged.
 */
export function tableTouchedAt(t) {
    return Math.max(t.updatedAt, t.lastRowAt);
}
/**
 * Which of two stamps is later. Equal reads as `same`, and so does a pair where
 * one side is missing — there is nothing to compare, and calling the present one
 * "newer" would invite a caller to delete the absent one.
 */
function laterOf(here, disk) {
    if (here === undefined || disk === undefined)
        return 'same';
    if (here > disk)
        return 'here';
    if (disk > here)
        return 'disk';
    return 'same';
}
/**
 * Pair the tables of two copies up.
 *
 * **By id first, then by name.** The id is the real identity and survives a
 * rename; the name catches the table that was deleted and re-made — the ordinary
 * refresh loop for anything backed by a URL — which comes back with a new id
 * under the old name. Matching on the id alone would call that pair two
 * unrelated tables and offer to keep both.
 *
 * Sorted by name, because the result is read as a list by a person.
 */
export function diffTables(here, disk) {
    const unmatched = new Map(disk.map((t) => [t.id, t]));
    const byName = new Map();
    for (const t of disk)
        if (!byName.has(t.name))
            byName.set(t.name, t);
    const out = [];
    for (const mine of here) {
        let theirs = unmatched.get(mine.id);
        if (!theirs) {
            const named = byName.get(mine.name);
            // Only if that one has not already been claimed by its own id.
            if (named && unmatched.has(named.id))
                theirs = named;
        }
        if (!theirs) {
            out.push({ name: mine.name, state: 'here-only', here: mine, newer: 'same' });
            continue;
        }
        unmatched.delete(theirs.id);
        const same = tableTouchedAt(mine) === tableTouchedAt(theirs) && mine.rows === theirs.rows;
        out.push({
            name: mine.name,
            state: same ? 'same' : 'differs',
            here: mine,
            disk: theirs,
            // Level stamps with different row counts have no newer side. That is the
            // one pair `newest` cannot settle, and saying `same` is what makes it fall
            // through to keeping both rather than picking one at random.
            newer: same ? 'same' : laterOf(tableTouchedAt(mine), tableTouchedAt(theirs)),
        });
    }
    for (const theirs of unmatched.values()) {
        out.push({ name: theirs.name, state: 'disk-only', disk: theirs, newer: 'same' });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
}
/**
 * The same pairing for the rows of ONE table, by id and by id only.
 *
 * A row has no name and no natural key this layer knows about, so an id that
 * appears on one side is a row the other side does not have. That is the whole
 * reason `newest` keeps both: which of "added here" and "deleted there" it was
 * cannot be recovered from what is stored.
 */
export function diffRows(here, disk) {
    const theirs = new Map(disk.map((r) => [r.id, r.updatedAt]));
    const out = [];
    for (const mine of here) {
        const at = theirs.get(mine.id);
        if (at === undefined) {
            out.push({ id: mine.id, state: 'here-only', hereAt: mine.updatedAt, newer: 'same' });
            continue;
        }
        theirs.delete(mine.id);
        const same = at === mine.updatedAt;
        out.push({
            id: mine.id,
            state: same ? 'same' : 'differs',
            hereAt: mine.updatedAt,
            diskAt: at,
            newer: same ? 'same' : laterOf(mine.updatedAt, at),
        });
    }
    for (const [id, at] of theirs)
        out.push({ id, state: 'disk-only', diskAt: at, newer: 'same' });
    return out;
}
/**
 * What to do about a difference when the user has not said.
 *
 * `newest` for anything that differs at all, and nothing for what already
 * matches. See {@link winnerOf} for why that one rule is also the union: a thing
 * only one side has has no rival to lose to, so `newest` keeps it and carries it
 * across.
 */
export function defaultChoice(state) {
    return state === 'same' ? 'skip' : 'newest';
}
/**
 * Which side wins one difference, or `null` for "touch neither copy".
 *
 * **`newest` never deletes.** A thing only one side has is not a thing that lost
 * — it has no rival — so the side that HAS it wins and it is carried to the
 * other. That is the whole difference between merging and overwriting, and it is
 * what makes "Take newest" safe to offer as the default: run it on two copies
 * that were both worked on and you get everything from both, with the clock
 * settling only the parts that genuinely collide.
 *
 * `null` is reached in two ways: `skip`, and a pair that differs with no newer
 * side — two copies stamped the same millisecond holding different numbers of
 * rows. There is no answer to that one, and inventing one would pick a winner at
 * random.
 *
 * An explicit `here` or `disk` is taken at its word, one-sided or not: a user
 * asking for one copy to be made to match the other is asking for exactly that,
 * deletions included.
 */
export function winnerOf(diff, choice) {
    if (choice === 'skip')
        return null;
    if (choice === 'here' || choice === 'disk')
        return choice;
    if (diff.newer !== 'same')
        return diff.newer;
    if (diff.state === 'here-only')
        return 'here';
    if (diff.state === 'disk-only')
        return 'disk';
    return null;
}
export function countDiffs(diffs) {
    const counts = { same: 0, differs: 0, hereOnly: 0, diskOnly: 0 };
    for (const d of diffs) {
        if (d.state === 'same')
            counts.same++;
        else if (d.state === 'differs')
            counts.differs++;
        else if (d.state === 'here-only')
            counts.hereOnly++;
        else
            counts.diskOnly++;
    }
    return counts;
}
/** True when the two copies hold the same things — nothing to settle. */
export function inStep(counts) {
    return counts.differs === 0 && counts.hereOnly === 0 && counts.diskOnly === 0;
}
function plural(n, noun) {
    return `${n} ${noun}${n === 1 ? '' : 's'}`;
}
/**
 * The comparison as one sentence: `2 tables differ, 1 only in the browser, 3 only in the
 * file`.
 *
 * Says nothing about the tables that MATCH — a list of what is fine is not what
 * the reader is deciding about. Empty when there is nothing to report, so the
 * caller can tell "in step" from "three differences" without counting again.
 */
export function describeDiffs(counts, noun = 'table') {
    const parts = [];
    if (counts.differs > 0)
        parts.push(`${plural(counts.differs, noun)} differ${counts.differs === 1 ? 's' : ''}`);
    if (counts.hereOnly > 0)
        parts.push(`${plural(counts.hereOnly, noun)} only in the browser`);
    if (counts.diskOnly > 0)
        parts.push(`${plural(counts.diskOnly, noun)} only in the file`);
    return parts.join(', ');
}
export function emptyRowPlan() {
    return { pull: [], push: [], dropHere: [], dropDisk: [] };
}
export function rowPlanIsEmpty(p) {
    return p.pull.length === 0 && p.push.length === 0 && p.dropHere.length === 0 && p.dropDisk.length === 0;
}
/**
 * Turn per-row answers into the four lists that get executed.
 *
 * A row's winner decides which way it moves, and its STATE decides whether
 * moving it means a copy or a delete: `here-only` losing to the file is a row
 * the file does not have, so bringing that side into line means deleting it.
 *
 * `choiceFor` is asked per row rather than passed as one value, because the
 * record-level dialog lets the user answer them one at a time.
 */
export function planRows(diffs, choiceFor) {
    const plan = emptyRowPlan();
    for (const d of diffs) {
        if (d.state === 'same')
            continue;
        const winner = winnerOf(d, choiceFor(d));
        if (winner === null)
            continue;
        if (winner === 'disk') {
            if (d.state === 'here-only')
                plan.dropHere.push(d.id);
            else
                plan.pull.push(d.id);
        }
        else {
            if (d.state === 'disk-only')
                plan.dropDisk.push(d.id);
            else
                plan.push.push(d.id);
        }
    }
    return plan;
}
//# sourceMappingURL=replicate.js.map